import type { Translations } from "../keys.js";

export const en = {
  eyebrow: "IBKR Activity Statement",
  title: "Profit Ratio Report",
  uploadMark: "Flex XML",
  uploadTitle: "Drop or choose IBKR XML",
  uploadBody: "Names, accounts, and addresses are masked locally.",
  nfaDisclaimer: "For education and trade review only. Not financial advice.",
  chooseFile: "Choose file",
  account: "Account",
  name: "Name",
  neutralName: "momo",
  period: "Period",
  baseCurrency: "Base currency",
  closed: "closed",
  executions: "executions",
  netRealized: "Net realized",
  profitFactor: "Profit Factor",
  winRate: "Win rate",
  payoffRatio: "Payoff ratio",
  expectancy: "Expectancy",
  executionRecords: "Executions",
  canceledOrders: "Canceled orders",
  medianHoldingDays: "Median holding days",
  commissionDrag: "Commissions",
  closedTradeSum: "Closed trade total",
  grossProfitLoss: "Gross profit / gross loss",
  averageWinLoss: "Avg win / avg loss",
  perClosedTrade: "Per closed trade",
  flexTradeRecords: "Trade records in Flex XML",
  flexCanceledRecords: "Canceled Order records in Flex XML",
  noCanceledRecords: "No canceled orders in this XML",
  holdingReview: "Holding",
  holdingReviewTitle: "Holding time and payoff analysis",
  holdingReviewHint: "Matches opens to closes, then compares PF, payoff, and win rate by holding period.",
  holdingPeriod: "Holding period",
  holdingIntraday: "Intraday",
  holdingSwing: "1-7 days",
  holdingPosition: "8-30 days",
  holdingLongTerm: "30+ days",
  directionReview: "Direction",
  directionReviewTitle: "Long / short position analysis",
  directionReviewHint: "Splits closed trades by strategy exposure to show which side has the edge.",
  longPosition: "Long",
  shortPosition: "Short",
  neutralPosition: "Neutral",
  equityCurve: "Equity Curve",
  dailyRealized: "Daily realized P/L",
  payoff: "Payoff",
  winLossDistribution: "Win/loss distribution",
  periodPerformance: "Period",
  periodHint: "Green bars are profit, red bars are loss. PF and payoff are in the table below.",
  weekly: "Weekly",
  monthly: "Monthly",
  periodColumn: "Period",
  monthColumn: "Month",
  realized: "Realized",
  pf: "PF",
  payoffShort: "Payoff",
  trades: "Trades",
  assetBreakdown: "Assets",
  assetBreakdownTitle: "Stock / option stats",
  asset: "Asset",
  medianPnl: "Median P/L",
  average: "Average",
  optionReview: "Options",
  optionReviewTitle: "Option review by underlying",
  underlying: "Underlying",
  date: "Date",
  dateCount: "Dates",
  daysShort: "d",
  hoursShort: "h",
  minutesShort: "m",
  weakestSession: "Weakest session",
  intradaySessionNoData: "No regular-session closes",
  intradaySessionTitle: "Open / midday / close win-rate analysis",
  intradaySessionHint: "Groups closes by market session to compare realized P/L, median P/L, average trade, PF, payoff, and win rate.",
  weekdayReviewTitle: "Weekday trading performance",
  weekdayReviewHint: "Compares regular-session closes and auto expiries by weekday to show the best and worst payoff ratio.",
  weekday: "Weekday",
  weekdaySunday: "Sunday",
  weekdayMonday: "Monday",
  weekdayTuesday: "Tuesday",
  weekdayWednesday: "Wednesday",
  weekdayThursday: "Thursday",
  weekdayFriday: "Friday",
  weekdaySaturday: "Saturday",
  bestDay: "Best",
  worstDay: "Worst",
  session: "Session",
  sessionMorning: "Open",
  sessionMidday: "Midday",
  sessionLate: "Close",
  autoExpiry: "Auto expiry",
  optionLifecycle: "Expiry / settlement",
  optionLifecycleRegularClose: "Regular close",
  optionLifecycleEp: "Expired",
  optionLifecycleEx: "Exercise",
  optionLifecycleA: "Assignment",
  optionLifecycleCD: "Cash settlement",
  optionLifecycleAEx: "Automatic exercise",
  optionLifecycleMEx: "Manual exercise",
  optionLifecycleGEA: "Offset expiry / assignment",
  discipline: "Discipline",
  disciplineRisk: "Discipline risk",
  largeLossAlert: "Large loss alert",
  largeLossDiscipline: "Pause first. Protect discipline.",
  largeLossRule: "This day's total loss is above the current risk threshold. Do not size up to recover before reviewing the planned stop, max daily loss, and pause rule.",
  worstLossDay: "Worst loss day",
  symbols: "Symbols",
  symbolPerformance: "Symbol leaderboard",
  page: "Page",
  previousPage: "Previous page",
  nextPage: "Next page",
  offlineCoach: "Offline Coach",
  offlineAdvice: "Offline advice",
  bestLoserWins: "Best Loser Wins",
  theme: "Theme",
  language: "Language",
  dark: "Dark",
  light: "Light",
  stock: "Stock",
  option: "Option",
  other: "Other",
  cash: "Cash",
  future: "Future",
  unknown: "Unknown",
  winBucketSmall: "Small win",
  winBucketMedium: "Mid win",
  winBucketLarge: "Large win",
  lossBucketSmall: "Small loss",
  lossBucketMedium: "Mid loss",
  lossBucketLarge: "Large loss",
  chartEmpty: "No closed trades to chart",
  dailyChartLegend: "bars: daily, line: cumulative",
  periodChartLegend: "PF, ref 1.00, cap",
  importFailed: "Import failed",
  profitStatus: "Profit",
  lossStatus: "Loss",
  flatStatus: "Flat",
  costStatus: "Cost",
  toggleDetails: "Toggle details",
  "error.invalid_flex_xml": "Upload an IBKR Flex XML file.",
  "advice.profit_factor_below_one.title": "Profit factor is below 1",
  "advice.profit_factor_below_one.body": "PF is {profitFactor}. Total gains are not covering total losses yet. Focus first on reducing large losses, expiry losses, and uncontrolled add-ons before increasing trade count.",
  "advice.profit_factor_thin_edge.title": "Profit factor has a thin edge",
  "advice.profit_factor_thin_edge.body": "PF is {profitFactor}. This is tradable, but one or two uncontrolled losses can erase the whole period. Define max daily loss, latest exit time, and pause rules first.",
  "advice.profit_factor_healthy.title": "Profit factor structure looks healthier",
  "advice.profit_factor_healthy.body": "PF is {profitFactor}. Do not blindly increase frequency. Identify which symbols, sessions, and setups are repeatable, then keep only the higher-quality trades.",
  "advice.winrate_and_payoff_weak.title": "Win rate and payoff cannot both be weak",
  "advice.winrate_and_payoff_weak.body": "Win rate is {winRate} and payoff ratio is {payoffRatio}. That means neither hit rate nor average reward is compensating enough. Confirm target room and stop distance before entry.",
  "advice.high_winrate_weak_payoff.title": "Win rate is patching a weak payoff structure",
  "advice.high_winrate_weak_payoff.body": "Win rate is {winRate}, but payoff ratio is {payoffRatio}. One bad trade can consume several good trades. This is usually an exit and risk-definition issue, not just a direction issue.",
  "advice.overtrade_loss_days.title": "Losing days show acceleration",
  "advice.overtrade_loss_days.body": "{days} days had both high trade count and negative PnL. Review trades after the first loss separately: were they still part of the plan, or attempts to win it back?",
  "advice.auto_expiry_losses.title": "Auto-expiry losses are not noise",
  "advice.auto_expiry_losses.body": "{count} auto-expiry losses were included. Expiry-to-zero should be treated as manageable risk, not an end-of-day accident. Write down the latest acceptable exit time.",
  "advice.good_loser_base.title": "A good loser protects the next trade",
  "advice.good_loser_base.body": "Average win is {avgWin} and average loss is {avgLoss}. Losses inside the plan are business costs; losses outside the plan mean risk must be narrowed.",
  "advice.detach_outcome_from_ego.title": "Separate PnL from ego",
  "advice.detach_outcome_from_ego.body": "During review, ask first: did this loss happen according to plan? If yes, it is cost. If no, it is a discipline problem.",
  "advice.large_tail_losses.title": "Reduce the largest losses first",
  "advice.large_tail_losses.body": "{count} losses exceeded {threshold}. The fastest way to improve PF is usually not raising win rate, but cutting the tail losses that destroy a whole week of progress.",
  "advice.asset_group_relative_edge.title": "{stronger} currently show a relative edge",
  "advice.asset_group_relative_edge.body": "Option PF is {optionProfitFactor}; stock PF is {stockProfitFactor}. Put capital and attention where PF is more stable, then check whether the weaker bucket is just occasional testing or emotional trading.",
  "advice.option_sample_dominant.title": "The current sample is mainly options",
  "advice.option_sample_dominant.body": "Option PF is {optionProfitFactor} with {optionWinRate} win rate. Review expiry, assignment/exercise, final-session decisions, and IV changes separately instead of only looking at total PnL.",
  "advice.stock_sample_dominant.title": "The current sample is mainly stocks",
  "advice.stock_sample_dominant.body": "Stock PF is {stockProfitFactor} with {stockWinRate} win rate. Check holding time, stop consistency, and chase entries before mixing stock results with options.",
  "advice.weak_option_underlying_days.title": "Review weak option symbol-days separately",
  "advice.weak_option_underlying_days.body": "{rows}. These combinations are closer to the real decision context than the summary table. Check direction, entry timing, and proximity to expiry first.",
  "advice.clean_sample.title": "Keep the sample clean",
  "advice.clean_sample.body": "No single asset group is clearly dragging the result. Keep collecting clean closed-trade samples, then review cyclicality, symbol concentration, and setup stability.",
} satisfies Translations;
